/**
 * What sits beside a BAM IQ conversation, in the panel and on the #/iq page:
 *
 *   ThreadList   the coach's conversations: pinned first, then Today, This
 *                week and Earlier, with search, pin and delete
 *   MemoryCard   "What BAM IQ knows about you": the switch, what's saved, and
 *                the coach's own edits (each checked by the server, never
 *                anything about a player)
 */
import React, { useState } from "react";
import { Pin, PinOff, Trash2, Search, Plus } from "lucide-react";
import { GOLD } from "../constants/theme";
import { Select } from "../shared/Pickers";

const ff = "'DM Sans', sans-serif";
const DAY = 86_400_000;

/** Pinned, Today, This week, Earlier; empty groups left out. */
export function groupThreads(threads, pins = [], now = Date.now()) {
  const pinned = new Set(pins);
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const today = start.getTime();
  const groups = [
    { label: "Pinned", items: pins.map(id => threads.find(t => t.id === id)).filter(Boolean) },
    { label: "Today", items: [] }, { label: "This week", items: [] }, { label: "Earlier", items: [] },
  ];
  for (const t of threads) {
    if (pinned.has(t.id)) continue;
    const at = new Date(t.last_activity_at || 0).getTime();
    groups[at >= today ? 1 : at >= today - 6 * DAY ? 2 : 3].items.push(t);
  }
  return groups.filter(g => g.items.length);
}

const sectionLabel = (C) => ({ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: C.textMid, margin: "2px 0 6px" });
const iconBtn = (C) => ({ display: "grid", placeItems: "center", width: 26, height: 26, border: "none", background: "transparent", color: C.textMid, cursor: "pointer", borderRadius: 6 });

export function ThreadList({ iq, C, onPick }) {
  const [q, setQ] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const pins = iq.pins || [];
  const term = q.trim().toLowerCase();
  const threads = (iq.threads || []).filter(t => !term || String(t.title || "").toLowerCase().includes(term));
  const groups = groupThreads(threads, pins.filter(id => threads.some(t => t.id === id)));
  return (
    <div style={{ display: "grid", gap: 14, fontFamily: ff }}>
      {(iq.threads || []).length > 4 && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 9, border: `1px solid ${C.border}`, background: C.inputBg }}>
          <Search size={14} color={C.textMid}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations" aria-label="Search conversations"
            style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", color: C.text, fontFamily: ff, fontSize: 13 }}/>
        </label>
      )}
      {!(iq.threads || []).length && <div style={{ fontSize: 13, color: C.textMid }}>No conversations yet.</div>}
      {term && !threads.length && <div style={{ fontSize: 13, color: C.textMid }}>Nothing matches "{q.trim()}".</div>}
      {groups.map(g => (
        <div key={g.label}>
          <div style={sectionLabel(C)}>{g.label}</div>
          <div style={{ display: "grid", gap: 2 }}>
            {g.items.map(t => {
              const pinned = pins.includes(t.id);
              return (
                <div key={t.id} className="bamiq-thread-row" style={{ display: "flex", alignItems: "center", gap: 2, borderRadius: 8, background: t.id === iq.threadId ? `${GOLD}18` : "transparent" }}>
                  <button type="button" data-thread={t.id} onClick={() => onPick(t.id)}
                    style={{ flex: 1, minWidth: 0, textAlign: "left", padding: "8px 10px", border: "none", background: "transparent", color: C.text,
                      fontFamily: ff, fontSize: 13.5, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title || "Conversation"}</button>
                  {confirmId === t.id ? (
                    <span style={{ display: "flex", gap: 4, paddingRight: 4 }}>
                      <button type="button" onClick={() => { iq.removeThread(t.id); setConfirmId(null); }} style={{ border: "none", background: "#E57373", color: "#111", borderRadius: 6, padding: "3px 7px", fontFamily: ff, fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>Delete</button>
                      <button type="button" onClick={() => setConfirmId(null)} style={{ border: `1px solid ${C.border}`, background: "transparent", color: C.text, borderRadius: 6, padding: "3px 7px", fontFamily: ff, fontSize: 11.5, cursor: "pointer" }}>Keep</button>
                    </span>
                  ) : (
                    <span className="bamiq-row-tools" style={{ display: "flex", ...(pinned ? { opacity: 1 } : {}) }}>
                      {iq.pin && (
                        <button type="button" aria-label={pinned ? `Unpin "${t.title || "conversation"}"` : `Pin "${t.title || "conversation"}"`} title={pinned ? "Unpin" : "Pin"}
                          onClick={() => iq.pin(t.id, !pinned)} style={{ ...iconBtn(C), color: pinned ? GOLD : C.textMid }}>
                          {pinned ? <PinOff size={13}/> : <Pin size={13}/>}
                        </button>
                      )}
                      <button type="button" aria-label={`Delete "${t.title || "conversation"}"`} title="Delete" onClick={() => setConfirmId(t.id)} style={iconBtn(C)}><Trash2 size={13}/></button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function MemoryRow({ item, iq, C }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.value);
  const [problem, setProblem] = useState(null);
  const save = async () => {
    const v = value.trim();
    if (!v || v === item.value) { setEditing(false); setValue(item.value); return; }
    const err = await iq.setMemoryItem(item.key, v);
    if (err) setProblem(err); else { setProblem(null); setEditing(false); }
  };
  return (
    <div data-memory={item.key} style={{ display: "grid", gap: 4, padding: "8px 10px", borderRadius: 9, background: C.bgHover }}>
      <div style={{ fontSize: 11.5, color: C.textMid }}>{item.label}</div>
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); save(); }} style={{ display: "flex", gap: 6 }}>
          <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} aria-label={item.label} maxLength={200}
            onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setEditing(false); setValue(item.value); setProblem(null); } }}
            style={{ flex: 1, minWidth: 0, padding: "6px 8px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontFamily: ff, fontSize: 13 }}/>
          <button type="submit" style={{ border: "none", background: GOLD, color: "#111", borderRadius: 7, padding: "0 10px", fontFamily: ff, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Save</button>
        </form>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: C.text }}>{item.value}</span>
          <button type="button" onClick={() => setEditing(true)} style={linkBtn(C)}>Edit</button>
          <button type="button" onClick={() => iq.forget(item.key)} style={linkBtn(C)}>Forget</button>
        </div>
      )}
      {problem && <div role="alert" style={{ fontSize: 12, color: "#E57373" }}>{problem}</div>}
    </div>
  );
}

const linkBtn = (C) => ({ border: "none", background: "transparent", color: C.textMid, cursor: "pointer", fontFamily: ff, fontSize: 12, textDecoration: "underline", padding: 0 });

export function MemoryCard({ iq, C }) {
  const [adding, setAdding] = useState(false);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState(null);
  const memory = iq.memory || { enabled: false, items: [] };
  const fields = iq.fields || {};
  const unset = Object.keys(fields).filter(k => !memory.items.some(i => i.key === k));
  const add = async () => {
    if (!key || !value.trim()) return;
    const err = await iq.setMemoryItem(key, value.trim());
    if (err) { setProblem(err); return; }
    setProblem(null); setAdding(false); setKey(""); setValue("");
  };
  return (
    <div style={{ display: "grid", gap: 10, fontFamily: ff }}>
      <div style={sectionLabel(C)}>What BAM IQ knows about you</div>
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: C.text, cursor: "pointer", lineHeight: 1.45 }}>
        <input id="bamiq-memory" type="checkbox" checked={memory.enabled} onChange={(e) => iq.setMemoryOn(e.target.checked)} style={{ marginTop: 3 }}/>
        <span>Let BAM IQ offer to remember your coaching context (level, ages, how you like answers). Nothing is saved until you tap Remember, and never anything about a player.</span>
      </label>
      {memory.items.length > 0 && <div style={{ display: "grid", gap: 6 }}>{memory.items.map(i => <MemoryRow key={i.key} item={i} iq={iq} C={C}/>)}</div>}
      {memory.enabled && !memory.items.length && !adding && <div style={{ fontSize: 12.5, color: C.textMid }}>Nothing saved yet. Answers get more specific as you tell it about your team.</div>}
      {memory.enabled && iq.setMemoryItem && unset.length > 0 && (adding ? (
        <form onSubmit={(e) => { e.preventDefault(); add(); }} style={{ display: "grid", gap: 6, padding: 10, borderRadius: 9, border: `1px dashed ${C.border}` }}>
          <Select value={key} onChange={(e) => setKey(e.target.value)} aria-label="What to add"
            style={{ padding: "6px 8px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontFamily: ff, fontSize: 13 }}>
            <option value="">Choose…</option>
            {unset.map(k => <option key={k} value={k}>{fields[k]}</option>)}
          </Select>
          <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. U14 girls" aria-label="Value" maxLength={200}
            style={{ padding: "6px 8px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontFamily: ff, fontSize: 13 }}/>
          {problem && <div role="alert" style={{ fontSize: 12, color: "#E57373" }}>{problem}</div>}
          <div style={{ display: "flex", gap: 6 }}>
            <button type="submit" disabled={!key || !value.trim()} style={{ border: "none", background: GOLD, color: "#111", borderRadius: 7, padding: "6px 12px", fontFamily: ff, fontSize: 12.5, fontWeight: 800, cursor: "pointer", opacity: key && value.trim() ? 1 : 0.5 }}>Save</button>
            <button type="button" onClick={() => { setAdding(false); setProblem(null); }} style={{ border: `1px solid ${C.border}`, background: "transparent", color: C.text, borderRadius: 7, padding: "6px 12px", fontFamily: ff, fontSize: 12.5, cursor: "pointer" }}>Cancel</button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, justifySelf: "start", ...linkBtn(C), textDecoration: "none", fontWeight: 700 }}>
          <Plus size={13}/> Add something about how you coach
        </button>
      ))}
    </div>
  );
}
